<template>
  <BaseModal
    :is-open="showModal"
    class="simulation-modal"
    :title
    description="Simulation setup wizard."
    @close="showModal = false"
  >
    <div v-if="!isCreated">
      <BaseSelect
        v-if="!error"
        v-model="templateId"
        :options="templateOptions"
        :disabled="isInProgress"
      />
      <p v-if="error" class="server-error">{{ error }}</p>
    </div>

    <div v-else-if="isCreated && newTeams.length > 0">
      <table class="teams-table">
        <thead>
          <tr>
            <th scope="col">Team</th>
            <th scope="col">Password</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(team, index) in newTeams" :key="index">
            <td>{{ team.name }}</td>
            <td>{{ team.pw }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <template #actions>
      <template v-if="!isCreated && !error">
        <BaseButton variant="outlined" :disabled="isInProgress" @click="ok">Cancel</BaseButton>
        <BaseButton :loading="isInProgress" @click="simulation(templateSession)">
          Create
        </BaseButton>
      </template>
      <BaseButton v-else @click="ok">Close</BaseButton>
    </template>
  </BaseModal>
</template>

<script>
import BaseButton from "@/components-ng/BaseButton.vue";
import BaseModal from "@/components-ng/BaseModal.vue";
import BaseSelect from "@/components-ng/BaseSelect.vue";
import serverCaller from "@/components/ServerCaller.vue";
import { SessionsHandler } from "@/handlers/sessionsHandler";
import { createHandlerContext } from "@/stores/context";

export default {
  name: "SimulationModal",
  components: { BaseSelect, BaseModal, BaseButton },
  mixins: [serverCaller],
  emits: ["changed"],
  data() {
    return {
      availableTemplateSessions: [],
      templateId: -1,
      newTeams: [],
      title: "Please select the simulation you want to create",
      isInProgress: false,
      isCreated: false,
      error: "",
      showModal: false,
    };
  },
  computed: {
    sessionsHandler() {
      return new SessionsHandler(createHandlerContext());
    },
    templateOptions() {
      return this.availableTemplateSessions.map((template) => ({
        label: template.name,
        value: template.id,
      }));
    },
    templateSession() {
      return this.availableTemplateSessions.find(({ id }) => id === this.templateId);
    },
  },
  mounted() {
    this.getAvailableTemplateSessions();
  },
  methods: {
    show() {
      this.initialize();
      this.showModal = true;
    },
    ok() {
      this.showModal = false;
    },
    fail(message) {
      this.error = message;
    },
    async simulation(type) {
      if (this.isInProgress) {
        return;
      }
      this.isInProgress = true;

      try {
        const result = await this.sessionsHandler.createTemplateSession(type.id);
        if (result.success) {
          this.$emit("changed");
          this.displayNewTeams(result.new_teams);
        } else {
          this.fail(result.error);
        }
      } catch {
        this.fail("Could not create the simulation. Please try again.");
      } finally {
        this.isInProgress = false;
      }
    },
    displayNewTeams(newTeams) {
      this.isCreated = true;
      this.newTeams = newTeams;
      this.ok();
    },
    getAvailableTemplateSessions() {
      this.sessionsHandler.getTemplateSessions().then((sessions) => {
        this.availableTemplateSessions = sessions
          .map((session) => (session.name ? session : processOldStyle(session)))
          .filter((session) => session.selectable);
        this.templateId = this.availableTemplateSessions[0].id;
      });

      //TODO remove as soon as backend is adjusted
      function processOldStyle(session) {
        return {
          id: session,
          name: session.charAt(0).toUpperCase() + session.slice(1),
          selectable: !["empty", "playaround"].includes(session),
        };
      }
    },

    initialize() {
      this.isInProgress = false;
      this.isCreated = false;
      this.newTeams = [];
      this.error = "";
      this.title = "Please select the simulation you want to create";
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/colors" as c;

.teams-table {
  border-collapse: collapse;
  width: 100%;
  margin-top: 16px;
  font-size: 0.875rem;

  th,
  td {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid c.$border-subtle;
  }
}

.server-error {
  color: c.$red;
  font-size: 0.875rem;
  margin-top: 12px;
}
</style>
